import {
  useContext,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Autocomplete,
  AutocompleteStateContext,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Text,
  TextField,
} from 'react-aria-components';

import { Button } from '../Button/Button.tsx';
import styles from './SearchPicker.module.css';

/**
 * Where a search is. The parent runs the search (a POST to the API; search terms never go
 * in URLs) and tells the picker what happened:
 *
 * - `idle`: nothing searched yet, or the text is shorter than `minChars`.
 * - `searching`: a search for the current text is in flight.
 * - `ready`: `items` are the results for `itemsQuery`.
 * - `error`: the search failed; `errorMessage` says what happened and what to do.
 * - `offline`: the device has no connection, so no search can run.
 */
export type SearchPickerStatus = 'idle' | 'searching' | 'ready' | 'error' | 'offline';

export interface SearchPickerProps<T> {
  /** Always visible, above the field. */
  label: string;
  /** Helper text below the field, e.g. what can be searched. */
  description?: string;
  placeholder?: string;
  /** The text in the field. Controlled, so the parent can search as it changes. */
  inputValue: string;
  onInputChange: (value: string) => void;
  status: SearchPickerStatus;
  /** Results, best match first. Shown in exactly this order; the picker never re-sorts. */
  items: readonly T[];
  /**
   * The text these items answer. Results for any other text are stale and hidden, so a
   * slow response can never be picked for a newer search. Defaults to `inputValue`.
   */
  itemsQuery?: string;
  getKey: (item: T) => string;
  /** Plain text for the option's accessible name. */
  getTextValue: (item: T) => string;
  /** The row's content. Show enough to tell similar results apart. */
  renderItem: (item: T) => ReactNode;
  onSelect: (item: T) => void;
  /**
   * An exact match on an identifier, like a scanned barcode or a typed patient number.
   * When the person presses Enter without choosing a row and exactly one result is an
   * exact match, it is chosen. Fuzzy matches are never chosen by Enter alone.
   */
  isExactMatch?: (item: T, query: string) => boolean;
  /** Characters needed before searching. DESIGN.md §7: two. */
  minChars?: number;
  /** Shown before the person has typed enough. */
  promptMessage?: string;
  /** Shown when a search returns nothing: say what to try. */
  emptyMessage: string;
  /** One action for the empty state, e.g. "Register new patient". */
  emptyAction?: ReactNode;
  errorMessage?: string;
  onRetry?: () => void;
  offlineMessage?: string;
  /** Announced when results arrive, e.g. `(n) => `${n} patients found``. */
  countMessage?: (count: number) => string;
  autoFocus?: boolean;
  /** Extra keyboard handling on the field, e.g. to close a palette. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** To put focus back in the field, e.g. after a check-in. */
  inputRef?: Ref<HTMLInputElement>;
}

const DEFAULT_MIN_CHARS = 2;

function singleExactMatch<T>(
  candidates: readonly T[],
  query: string,
  isExactMatch: ((item: T, query: string) => boolean) | undefined,
): T | undefined {
  if (isExactMatch === undefined) return undefined;
  const exact = candidates.filter((item) => isExactMatch(item, query));
  return exact.length === 1 ? exact[0] : undefined;
}

/**
 * When the results change, a highlighted row that is no longer shown must stop being the
 * field's active descendant; otherwise screen readers point at nothing and Enter is
 * swallowed by a row that isn't there.
 */
function ClearStaleHighlight({ keys }: { keys: string }) {
  const state = useContext(AutocompleteStateContext);
  useEffect(() => {
    const id = state?.focusedNodeId ?? null;
    if (id !== null && document.getElementById(id) === null) state?.setFocusedNodeId(null);
  }, [keys, state]);
  return null;
}

function hasLiveActiveDescendant(input: HTMLInputElement): boolean {
  const id = input.getAttribute('aria-activedescendant');
  return id !== null && id !== '' && document.getElementById(id) !== null;
}

/**
 * Type-to-search picker for patients, drugs, diagnoses and tests (DESIGN.md §7): results
 * appear after two characters, in 44px rows, below the field, and are navigable with the
 * arrow keys. Enter chooses the highlighted row; with no row highlighted it chooses only
 * a single exact match, so a barcode scan followed by Enter works but a fuzzy name never
 * gets picked by accident.
 */
export function SearchPicker<T>({
  label,
  description,
  placeholder,
  inputValue,
  onInputChange,
  status,
  items,
  itemsQuery,
  getKey,
  getTextValue,
  renderItem,
  onSelect,
  isExactMatch,
  minChars = DEFAULT_MIN_CHARS,
  promptMessage,
  emptyMessage,
  emptyAction,
  errorMessage,
  onRetry,
  offlineMessage,
  countMessage,
  autoFocus = false,
  onKeyDown,
  inputRef,
}: SearchPickerProps<T>) {
  const listLabelId = useId();
  const query = inputValue.trim();
  const tooShort = query.length < minChars;
  const current = status === 'ready' && !tooShort && (itemsQuery ?? inputValue).trim() === query;
  const visible = current ? items : [];

  // A scanner types fast and presses Enter before results arrive. Remember the Enter
  // for this text and act on it when its results do.
  const pendingEnter = useRef<string | null>(null);
  const latest = useRef({ onSelect, isExactMatch });
  useEffect(() => {
    latest.current = { onSelect, isExactMatch };
  }, [onSelect, isExactMatch]);

  useEffect(() => {
    if (pendingEnter.current === null) return;
    if (pendingEnter.current !== query) {
      pendingEnter.current = null;
      return;
    }
    if (!current && (status === 'searching' || status === 'idle')) return;
    pendingEnter.current = null;
    if (!current) return;
    const match = singleExactMatch(items, query, latest.current.isExactMatch);
    if (match !== undefined) latest.current.onSelect(match);
  }, [query, status, current, items]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.key !== 'Enter') return;
    // A highlighted row takes Enter itself (aria-activedescendant is set on the field).
    if (hasLiveActiveDescendant(event.currentTarget)) return;
    if (tooShort) return;
    event.preventDefault();
    if (current) {
      const match = singleExactMatch(visible, query, isExactMatch);
      if (match !== undefined) onSelect(match);
    } else if (status === 'searching' || status === 'idle') {
      pendingEnter.current = query;
    }
  };

  let message: ReactNode = null;
  if (status === 'offline') {
    message = (
      <p className={styles.message}>
        {offlineMessage ?? 'Search needs a connection to Wodi. Reconnect, then search again.'}
      </p>
    );
  } else if (tooShort) {
    message = promptMessage === undefined ? null : <p className={styles.hint}>{promptMessage}</p>;
  } else if (status === 'searching' || (status === 'ready' && !current)) {
    message = <p className={styles.message}>Searching…</p>;
  } else if (status === 'error') {
    message = (
      <div className={styles.error} role="alert">
        <p>{errorMessage ?? 'Search did not work. Try again.'}</p>
        {onRetry === undefined ? null : (
          <Button dense onPress={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  } else if (current && visible.length === 0) {
    message = (
      <div className={styles.empty}>
        <p>{emptyMessage}</p>
        {emptyAction}
      </div>
    );
  }

  const announcement =
    current && visible.length > 0
      ? (countMessage?.(visible.length) ??
        `${String(visible.length)} ${visible.length === 1 ? 'result' : 'results'}`)
      : '';

  return (
    <div className={styles.picker}>
      <Autocomplete inputValue={inputValue} onInputChange={onInputChange} disableAutoFocusFirst>
        <TextField className={styles.field} type="search" autoFocus={autoFocus}>
          <Label className={styles.label}>{label}</Label>
          <Input
            {...(inputRef === undefined ? {} : { ref: inputRef })}
            className={styles.input}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
            {...(placeholder === undefined ? {} : { placeholder })}
          />
          {description === undefined ? null : (
            <Text slot="description" className={styles.description}>
              {description}
            </Text>
          )}
        </TextField>
        <span id={listLabelId} className={styles.visuallyHidden}>
          {`${label} results`}
        </span>
        <ListBox
          className={styles.list}
          aria-labelledby={listLabelId}
          items={visible}
          data-empty={visible.length === 0 || undefined}
          onAction={(key) => {
            const item = visible.find((candidate) => getKey(candidate) === key);
            if (item !== undefined) onSelect(item);
          }}
        >
          {(item) => (
            <ListBoxItem id={getKey(item)} textValue={getTextValue(item)} className={styles.item}>
              {renderItem(item)}
            </ListBoxItem>
          )}
        </ListBox>
        <ClearStaleHighlight keys={visible.map(getKey).join(' ')} />
      </Autocomplete>
      <div className={styles.visuallyHidden} role="status">
        {announcement}
      </div>
      {message}
    </div>
  );
}
